import React, { useState, useEffect, useCallback } from 'react';
import {
    Loader2, AlertTriangle, RefreshCw, ChevronDown, ChevronUp,
    Bug, Lightbulb, Star, MessageSquare, Send, Check,
    Archive, CheckCircle2, Circle, Clock, Filter, SortDesc,
    User, FileText, Mail, MailCheck, MailX, Lock,
} from 'lucide-react';
import { betaFeedback } from '../services/api';

/**
 * AdminFeedback — Full feedback triage panel (Phase 1B)
 *
 * Capabilities:
 *   - Expandable item cards with full detail + teacher identity
 *   - Status badge + cycle: open → in_progress → resolved → archived
 *   - Internal notes (admin-only, not sent to teacher)
 *   - Reply to teacher (stored in DB, optional email via SendGrid)
 *   - Email delivery status shown inline, never blocks reply save
 *   - Filter bar: status (default hides resolved/archived) + category
 *   - Sort: most-recent first / unresolved first toggle
 *   - Resolved/archived section in collapsed accordion at bottom
 *
 * API surface used (all pre-existing in api.js):
 *   betaFeedback.listCategorized()           GET  /api/beta-feedback/categorized
 *   betaFeedback.patchCategorized(id, body)  PATCH /api/beta-feedback/categorized/:id
 *   betaFeedback.replyCategorized(id, body)  POST  /api/beta-feedback/categorized/:id/reply
 */

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_ORDER = ['open', 'in_progress', 'resolved', 'archived'];
const STATUS_NEXT  = { open: 'in_progress', in_progress: 'resolved', resolved: 'archived', archived: 'open' };

const STATUS_META = {
    open:        { label: 'Open',        color: '#3b82f6', bg: '#eff6ff', icon: Circle },
    in_progress: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb', icon: Clock },
    resolved:    { label: 'Resolved',    color: '#10b981', bg: '#f0fdf4', icon: CheckCircle2 },
    archived:    { label: 'Archived',    color: '#94a3b8', bg: '#f8fafc', icon: Archive },
};

const CATEGORY_META = {
    bug:             { label: 'Bug',             color: '#ef4444', bg: '#fef2f2', icon: Bug },
    improvement:     { label: 'Improvement',     color: '#f59e0b', bg: '#fffbeb', icon: Lightbulb },
    feature_request: { label: 'Feature Request', color: '#8b5cf6', bg: '#f5f3ff', icon: Star },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(iso) {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || STATUS_META.open;
    const Icon = meta.icon;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
            color: meta.color, background: meta.bg,
            padding: '2px 8px', borderRadius: 6,
        }}>
            <Icon size={11} />
            {meta.label}
        </span>
    );
};

// ─── CategoryBadge ────────────────────────────────────────────────────────────

const CategoryBadge = ({ category }) => {
    const meta = CATEGORY_META[category] || { label: category, color: '#6b7280', bg: '#f1f5f9', icon: MessageSquare };
    const Icon = meta.icon;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
            color: meta.color, background: meta.bg,
            padding: '2px 8px', borderRadius: 4,
        }}>
            <Icon size={11} />
            {meta.label.replace(/_/g, ' ')}
        </span>
    );
};

// ─── FeedbackItemCard ─────────────────────────────────────────────────────────

const FeedbackItemCard = ({ item, onUpdate }) => {
    const [expanded, setExpanded]       = useState(false);
    const [notesValue, setNotesValue]   = useState(item.internal_notes || '');
    const [notesSaving, setNotesSaving] = useState(false);
    const [notesSaved, setNotesSaved]   = useState(false);
    const [replyText, setReplyText]     = useState(item.admin_reply || '');
    const [sendEmail, setSendEmail]     = useState(false);
    const [replying, setReplying]       = useState(false);
    const [replyResult, setReplyResult] = useState(null); // { saved, emailSent, emailError }
    const [statusLoading, setStatusLoading] = useState(false);

    // Keep local state in sync if item is updated externally
    useEffect(() => {
        setNotesValue(item.internal_notes || '');
        setReplyText(item.admin_reply || '');
    }, [item.internal_notes, item.admin_reply]);

    // ── Status cycle ──────────────────────────────────────────────────────
    const handleStatusCycle = async () => {
        const nextStatus = STATUS_NEXT[item.status] || 'open';
        setStatusLoading(true);
        try {
            await betaFeedback.patchCategorized(item.id, { status: nextStatus });
            onUpdate(item.id, { status: nextStatus });
        } catch (err) {
            console.error('Status update failed:', err);
        } finally {
            setStatusLoading(false);
        }
    };

    const handleSetStatus = async (targetStatus) => {
        if (targetStatus === item.status) return;
        setStatusLoading(true);
        try {
            await betaFeedback.patchCategorized(item.id, { status: targetStatus });
            onUpdate(item.id, { status: targetStatus });
        } catch (err) {
            console.error('Status update failed:', err);
        } finally {
            setStatusLoading(false);
        }
    };

    // ── Internal notes ────────────────────────────────────────────────────
    const handleNotesSave = async () => {
        if (notesValue === (item.internal_notes || '')) return;
        setNotesSaving(true);
        try {
            await betaFeedback.patchCategorized(item.id, { internal_notes: notesValue });
            onUpdate(item.id, { internal_notes: notesValue });
            setNotesSaved(true);
            setTimeout(() => setNotesSaved(false), 2500);
        } catch (err) {
            console.error('Notes save failed:', err);
        } finally {
            setNotesSaving(false);
        }
    };

    // ── Reply ─────────────────────────────────────────────────────────────
    const handleReply = async () => {
        if (!replyText.trim()) return;
        setReplying(true);
        setReplyResult(null);
        try {
            const res = await betaFeedback.replyCategorized(item.id, {
                admin_reply: replyText.trim(),
                send_email: sendEmail,
            });
            onUpdate(item.id, {
                admin_reply: replyText.trim(),
                admin_reply_at: res.data?.replied_at,
                replied_by: res.data?.replied_by,
            });
            setReplyResult({
                saved: res.data?.reply_saved,
                emailSent: res.data?.email_sent,
                emailError: res.data?.email_error,
                emailRequested: res.data?.email_requested,
            });
        } catch (err) {
            setReplyResult({ saved: false, emailSent: false, emailError: String(err) });
        } finally {
            setReplying(false);
        }
    };

    const isResolved = item.status === 'resolved' || item.status === 'archived';

    return (
        <div style={{
            background: '#fff',
            border: `1px solid ${isResolved ? '#e5e7eb' : '#d1d5db'}`,
            borderRadius: 12,
            overflow: 'hidden',
            opacity: isResolved ? 0.75 : 1,
            transition: 'opacity 0.2s',
        }}>
            {/* ── Card header (always visible) ── */}
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '14px 16px', cursor: 'pointer',
                    userSelect: 'none',
                    background: expanded ? '#f8fafc' : '#fff',
                    transition: 'background 0.15s',
                }}
            >
                {/* Left: category + status */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <CategoryBadge category={item.category} />
                    <StatusBadge status={item.status} />
                </div>

                {/* Center: message preview + identity */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: 14, color: '#111827', lineHeight: '1.45',
                        marginBottom: 6,
                        display: '-webkit-box', WebkitLineClamp: expanded ? 'unset' : 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                        {item.message}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {/* Teacher identity — primary Phase 1B requirement */}
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: 11, fontWeight: 500, color: '#6366f1',
                        }}>
                            <User size={11} />
                            {item.user_email || 'unknown'}
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                            {timeAgo(item.created_at)}
                        </span>
                        {item.admin_reply && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                fontSize: 11, color: '#10b981', fontWeight: 500,
                            }}>
                                <MailCheck size={11} /> Replied
                            </span>
                        )}
                    </div>
                </div>

                {/* Right: status cycle button + expand chevron */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleStatusCycle(); }}
                        disabled={statusLoading}
                        title={`Advance to: ${STATUS_NEXT[item.status]?.replace(/_/g, ' ')}`}
                        style={{
                            fontSize: 11, fontWeight: 600,
                            color: STATUS_META[item.status]?.color || '#6b7280',
                            background: STATUS_META[item.status]?.bg || '#f1f5f9',
                            border: `1px solid ${STATUS_META[item.status]?.color || '#6b7280'}30`,
                            borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4,
                            opacity: statusLoading ? 0.6 : 1,
                        }}
                    >
                        {statusLoading
                            ? <Loader2 size={11} style={{ animation: 'spin 0.8s linear infinite' }} />
                            : '→'}
                        {STATUS_NEXT[item.status]?.replace(/_/g, ' ')}
                    </button>
                    {expanded ? <ChevronUp size={16} color="#9ca3af" /> : <ChevronDown size={16} color="#9ca3af" />}
                </div>
            </div>

            {/* ── Expanded detail panel ── */}
            {expanded && (
                <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* Full message */}
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                            Full Message
                        </div>
                        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                            {item.message}
                        </p>
                    </div>

                    {/* Teacher identity row */}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Teacher</div>
                            <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <User size={12} /> {item.user_email || '—'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Submitted</div>
                            <div style={{ fontSize: 13, color: '#374151' }}>{fmtDate(item.created_at)}</div>
                        </div>
                        {item.resolved_at && (
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>
                                    {item.status === 'archived' ? 'Archived' : 'Resolved'}
                                </div>
                                <div style={{ fontSize: 13, color: '#374151' }}>{fmtDate(item.resolved_at)}</div>
                            </div>
                        )}
                    </div>

                    {/* Status changer — explicit buttons */}
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>
                            Status
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {STATUS_ORDER.map(s => {
                                const meta = STATUS_META[s];
                                const active = item.status === s;
                                return (
                                    <button
                                        key={s}
                                        onClick={() => handleSetStatus(s)}
                                        disabled={statusLoading || active}
                                        style={{
                                            fontSize: 11, fontWeight: 600,
                                            color: active ? '#fff' : meta.color,
                                            background: active ? meta.color : meta.bg,
                                            border: `1px solid ${meta.color}50`,
                                            borderRadius: 6, padding: '4px 10px',
                                            cursor: active ? 'default' : 'pointer',
                                            opacity: statusLoading ? 0.6 : 1,
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        {meta.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Internal notes (admin-only) */}
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Lock size={10} /> Internal Notes <span style={{ color: '#cbd5e1', fontWeight: 400, textTransform: 'none', fontSize: 10 }}>(not visible to teacher)</span>
                        </div>
                        <textarea
                            value={notesValue}
                            onChange={e => setNotesValue(e.target.value)}
                            onBlur={handleNotesSave}
                            placeholder="Add notes for the team — never sent to teacher…"
                            rows={3}
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                fontSize: 13, color: '#374151', lineHeight: 1.5,
                                padding: '10px 12px', borderRadius: 8,
                                border: '1px solid #e5e7eb', resize: 'vertical',
                                fontFamily: 'inherit', background: '#fafafa',
                                outline: 'none',
                            }}
                            onFocus={e => e.target.style.borderColor = '#6366f1'}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <button
                                onClick={handleNotesSave}
                                disabled={notesSaving || notesValue === (item.internal_notes || '')}
                                style={{
                                    fontSize: 11, fontWeight: 600,
                                    color: '#6366f1', background: '#eef2ff',
                                    border: 'none', borderRadius: 6, padding: '3px 10px',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                    opacity: (notesSaving || notesValue === (item.internal_notes || '')) ? 0.5 : 1,
                                }}
                            >
                                {notesSaving ? <Loader2 size={11} style={{ animation: 'spin 0.8s linear infinite' }} /> : <FileText size={11} />}
                                {notesSaving ? 'Saving…' : 'Save Notes'}
                            </button>
                            {notesSaved && (
                                <span style={{ fontSize: 11, color: '#10b981', display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Check size={11} /> Saved
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Existing reply display */}
                    {item.admin_reply && (
                        <div style={{ background: '#fffbeb', borderLeft: '3px solid #fbbf24', borderRadius: '0 8px 8px 0', padding: '10px 14px' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 4 }}>
                                Reply sent {item.admin_reply_at ? `· ${fmtDate(item.admin_reply_at)}` : ''} {item.replied_by ? `· by ${item.replied_by}` : ''}
                            </div>
                            <p style={{ fontSize: 13, color: '#78350f', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                {item.admin_reply}
                            </p>
                        </div>
                    )}

                    {/* Reply panel */}
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>
                            {item.admin_reply ? 'Update Reply' : 'Reply to Teacher'}
                        </div>
                        <textarea
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            placeholder="Write a reply to the teacher…"
                            rows={3}
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                fontSize: 13, color: '#374151', lineHeight: 1.5,
                                padding: '10px 12px', borderRadius: 8,
                                border: '1px solid #e5e7eb', resize: 'vertical',
                                fontFamily: 'inherit',
                                outline: 'none',
                            }}
                            onFocus={e => e.target.style.borderColor = '#6366f1'}
                        />

                        {/* Email toggle + send */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={sendEmail}
                                    onChange={e => setSendEmail(e.target.checked)}
                                    style={{ accentColor: '#6366f1' }}
                                />
                                <Mail size={12} />
                                Also send email to teacher
                            </label>

                            <button
                                onClick={handleReply}
                                disabled={replying || !replyText.trim()}
                                style={{
                                    fontSize: 12, fontWeight: 600,
                                    color: '#fff', background: replying ? '#a5b4fc' : '#6366f1',
                                    border: 'none', borderRadius: 8, padding: '7px 16px',
                                    cursor: (replying || !replyText.trim()) ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    transition: 'background 0.15s',
                                }}
                            >
                                {replying
                                    ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} />
                                    : <Send size={13} />}
                                {replying ? 'Sending…' : 'Send Reply'}
                            </button>
                        </div>

                        {/* Reply result status */}
                        {replyResult && (
                            <div style={{
                                marginTop: 8, padding: '8px 12px', borderRadius: 8,
                                background: replyResult.saved ? '#f0fdf4' : '#fef2f2',
                                fontSize: 12, color: replyResult.saved ? '#065f46' : '#991b1b',
                                display: 'flex', flexDirection: 'column', gap: 3,
                            }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {replyResult.saved ? <Check size={12} /> : <AlertTriangle size={12} />}
                                    {replyResult.saved ? 'Reply saved to database.' : 'Failed to save reply.'}
                                </span>
                                {replyResult.emailRequested && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: replyResult.emailSent ? '#065f46' : '#92400e' }}>
                                        {replyResult.emailSent ? <MailCheck size={12} /> : <MailX size={12} />}
                                        {replyResult.emailSent
                                            ? 'Email delivered.'
                                            : `Email not sent: ${replyResult.emailError || 'unknown error'}`}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminFeedback = () => {
    const [items, setItems]               = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState('');
    const [lastRefresh, setLastRefresh]   = useState(null);

    // Filter/sort state
    const [filterStatus, setFilterStatus]     = useState('active'); // 'active' = hide resolved+archived
    const [filterCategory, setFilterCategory] = useState('all');
    const [sortMode, setSortMode]             = useState('recent');  // 'recent' | 'unresolved'

    // Resolved/archived accordion
    const [showArchived, setShowArchived] = useState(false);

    // ── Load data ─────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await betaFeedback.listCategorized();
            setItems(res.data?.submissions || []);
            setLastRefresh(new Date());
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Access denied. Admin role required.');
            } else {
                setError(err.response?.data?.detail || 'Failed to load feedback.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    // ── Optimistic update ─────────────────────────────────────────────────
    const handleUpdate = useCallback((id, patch) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
    }, []);

    // ── Derive views ──────────────────────────────────────────────────────
    const filtered = items.filter(item => {
        const catMatch = filterCategory === 'all' || item.category === filterCategory;
        const statusMatch = filterStatus === 'all'
            ? true
            : filterStatus === 'active'
                ? !['resolved', 'archived'].includes(item.status)
                : item.status === filterStatus;
        return catMatch && statusMatch;
    });

    const sorted = [...filtered].sort((a, b) => {
        if (sortMode === 'unresolved') {
            const order = { open: 0, in_progress: 1, resolved: 2, archived: 3 };
            const diff = (order[a.status] ?? 4) - (order[b.status] ?? 4);
            if (diff !== 0) return diff;
        }
        return new Date(b.created_at) - new Date(a.created_at); // most recent first
    });

    // Split active vs resolved/archived for the accordion
    const activeItems   = sorted.filter(i => !['resolved', 'archived'].includes(i.status));
    const closedItems   = sorted.filter(i => ['resolved', 'archived'].includes(i.status));

    // All-items for counters
    const openCount       = items.filter(i => i.status === 'open').length;
    const inProgressCount = items.filter(i => i.status === 'in_progress').length;
    const resolvedCount   = items.filter(i => i.status === 'resolved').length;
    const archivedCount   = items.filter(i => i.status === 'archived').length;

    // ── Loading / error ────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, flexDirection: 'column', gap: 12 }}>
                <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading feedback…</span>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, flexDirection: 'column', gap: 12 }}>
                <AlertTriangle size={28} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <RefreshCw size={14} /> Retry
                </button>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Feedback Triage</h2>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                        {items.length} total · {openCount} open · {inProgressCount} in progress
                        {lastRefresh ? ` · refreshed ${lastRefresh.toLocaleTimeString()}` : ''}
                    </p>
                </div>
                <button
                    onClick={loadData}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 13, fontWeight: 500, color: '#6366f1',
                        background: '#eef2ff', border: 'none', borderRadius: 8,
                        padding: '8px 14px', cursor: 'pointer',
                    }}
                >
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {/* ── Status summary pills ── */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                {STATUS_ORDER.map(s => {
                    const meta = STATUS_META[s];
                    const count = items.filter(i => i.status === s).length;
                    return (
                        <div key={s} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: meta.bg, border: `1px solid ${meta.color}30`,
                            borderRadius: 8, padding: '5px 12px',
                            fontSize: 12, color: meta.color, fontWeight: 600,
                        }}>
                            <meta.icon size={12} />
                            {meta.label}: {count}
                        </div>
                    );
                })}
            </div>

            {/* ── Filter + sort bar ── */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                marginBottom: 20, padding: '12px 14px',
                background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb',
            }}>
                <Filter size={14} style={{ color: '#6b7280', flexShrink: 0 }} />

                {/* Status filter */}
                <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    style={{
                        fontSize: 12, fontWeight: 500, color: '#374151',
                        border: '1px solid #d1d5db', borderRadius: 6,
                        padding: '4px 8px', background: '#fff', cursor: 'pointer',
                    }}
                >
                    <option value="active">Active (open + in progress)</option>
                    <option value="all">All statuses</option>
                    <option value="open">Open only</option>
                    <option value="in_progress">In Progress only</option>
                    <option value="resolved">Resolved only</option>
                    <option value="archived">Archived only</option>
                </select>

                {/* Category filter */}
                <select
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                    style={{
                        fontSize: 12, fontWeight: 500, color: '#374151',
                        border: '1px solid #d1d5db', borderRadius: 6,
                        padding: '4px 8px', background: '#fff', cursor: 'pointer',
                    }}
                >
                    <option value="all">All categories</option>
                    <option value="bug">Bugs</option>
                    <option value="improvement">Improvements</option>
                    <option value="feature_request">Feature Requests</option>
                </select>

                {/* Sort toggle */}
                <button
                    onClick={() => setSortMode(m => m === 'recent' ? 'unresolved' : 'recent')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        fontSize: 12, fontWeight: 500,
                        color: sortMode === 'unresolved' ? '#6366f1' : '#6b7280',
                        background: sortMode === 'unresolved' ? '#eef2ff' : 'transparent',
                        border: '1px solid #d1d5db', borderRadius: 6,
                        padding: '4px 10px', cursor: 'pointer',
                    }}
                >
                    <SortDesc size={12} />
                    {sortMode === 'recent' ? 'Most Recent' : 'Unresolved First'}
                </button>

                <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>
                    Showing {filterStatus === 'active' ? activeItems.length : sorted.length} item{sorted.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* ── Item list: active items ── */}
            {filterStatus === 'active' || filterStatus === 'all' ? (
                <>
                    {activeItems.length === 0 ? (
                        <div style={{
                            textAlign: 'center', padding: '48px 0',
                            fontSize: 14, color: '#6b7280',
                            background: '#f8fafc', borderRadius: 12, border: '1px solid #e5e7eb',
                        }}>
                            <CheckCircle2 size={32} style={{ color: '#10b981', marginBottom: 8 }} />
                            <div style={{ fontWeight: 600 }}>All caught up!</div>
                            <div style={{ fontSize: 12, marginTop: 4 }}>No open or in-progress items.</div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {activeItems.map(item => (
                                <FeedbackItemCard key={item.id} item={item} onUpdate={handleUpdate} />
                            ))}
                        </div>
                    )}

                    {/* ── Resolved / Archived accordion ── */}
                    {closedItems.length > 0 && (
                        <div style={{ marginTop: 24 }}>
                            <button
                                onClick={() => setShowArchived(!showArchived)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                                    fontSize: 13, fontWeight: 600, color: '#6b7280',
                                    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10,
                                    padding: '10px 14px', cursor: 'pointer',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Archive size={14} />
                                    Resolved &amp; Archived
                                    <span style={{
                                        fontSize: 11, fontWeight: 700,
                                        color: '#94a3b8', background: '#e2e8f0',
                                        padding: '1px 7px', borderRadius: 10,
                                    }}>
                                        {resolvedCount + archivedCount}
                                    </span>
                                </span>
                                {showArchived ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {showArchived && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                                    {closedItems.map(item => (
                                        <FeedbackItemCard key={item.id} item={item} onUpdate={handleUpdate} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                /* Non-active filter: just show the sorted list directly */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {sorted.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 0', fontSize: 14, color: '#6b7280' }}>
                            No items match the current filter.
                        </div>
                    ) : sorted.map(item => (
                        <FeedbackItemCard key={item.id} item={item} onUpdate={handleUpdate} />
                    ))}
                </div>
            )}

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default AdminFeedback;
